import React, { Component } from 'react'
import { findDOMNode } from 'react-dom'
import { Link, withRouter } from 'react-router'
import { connect } from 'react-redux'
import { push } from 'react-router-redux'
import isEqual from 'lodash.isequal'
import lscache from 'lscache'

import classNames from './Album.css'
import { fetchAlbum, setFavouriteAlbum, setAlbumCover } from '../redux/album'
import { fetchAlbums } from '../redux/albums'
import { searchImages, fetchSearchOptions } from '../redux/search'
import {
  replaceStructure, emptyStructure, emptyQuery,
  selectImage, deselectImage, disableImageSelect,
  updateSortOrder, updateManualOrder, updateThumbSize,
  enableImageReorder, fixAlbum, unfixAlbum, excludeImages
} from '../redux/query'
import { SAVE_ALBUM_MODAL_KEY, openModal } from '../redux/modals'
import { getHash, isEmptyStructure, createImageGetter, hasDynamicLink } from '../utils/images'
import { pluralize } from '../utils/string'
import { userHasPerm, isAnonymous, canDownload } from '../utils/auth'
import { mapStateToLists } from '../utils/lists'
import Gallery from '../components/gallery/Gallery'
import ImageSearch from '../components/search/ImageSearch'
import AlbumMenu from '../components/album/AlbumMenu'
import SortMenu from '../components/album/SortMenu'
import AlbumHeader from '../components/album/AlbumHeader'
import AlbumInfo from '../components/album/AlbumInfo'
import AlbumShare from '../components/album/AlbumShare'
import AlbumLists from '../components/album/AlbumLists'
import AlbumBreadcrumbs from '../components/album/AlbumBreadcrumbs'
import ErrorMessage from '../components/ErrorMessage'
import LoadingSpinner from '../components/LoadingSpinner'
import Icon from '../components/Icon'
import { MOBILE_BREAKPOINT, QUERY_CACHE_KEY, QUERY_CACHE_EXPIRY, MAX_FIXED_COUNT, SPECIAL_ALBUM_PATH_TITLE } from '../constants'

const PAGINATION_SENSITIVITY = 40 // Higher means more images load earlier
const TOP_BAR_HEIGHT = 44 // Should match $header-height in variables.css

class Album extends Component {
  state = {
    sortMenuOpen: false,
    saveSuccess: false
  }
  componentDidMount () {
    const { location, params, album, fetchAlbum, replaceStructure } = this.props

    // Fetch album if we need to
    if (params.id && !album.id) {
      fetchAlbum(params.id).then(album => {
        if (!album) {
          return
        }
        const cached = lscache.get(QUERY_CACHE_KEY + params.id)
        if (params.task === 'edit' && cached) {
          replaceStructure(cached)
        } else {
          replaceStructure(JSON.parse(album.json_data))
        }
      })
    }

    // Replace query with album data if we already have it
    if (album.json_data) {
      replaceStructure(JSON.parse(album.json_data))
    }

    // Check query cache if mounting a search page
    if (location.pathname === '/search') {
      const cached = lscache.get(QUERY_CACHE_KEY)
      if (cached) {
        replaceStructure(cached)
      } else {
        replaceStructure(emptyStructure)
      }
    }

    if (location.query.path) {
      this.loadPath(location.query.path)
    }

    if (this.props.shouldFetchAlbums) {
      this.props.fetchAlbums()
    }

    if (params.task === 'info' && !this.props.keywordMap) {
      this.props.fetchSearchOptions()
    }

    this.props.router.setRouteLeaveHook(this.props.route, this.routerWillLeave)

    window.addEventListener('scroll', this.onScroll)
  }
  componentWillReceiveProps (nextProps) {
    const { location, params, album, hash, fetchAlbum, replaceStructure, searchImages, images } = this.props

    // Fetch album if we need to
    if (nextProps.params.id && !nextProps.album.id && !nextProps.album.fetching && !nextProps.album.fetchError) {
      fetchAlbum(nextProps.params.id).then(album => {
        if (!album) return
        replaceStructure(JSON.parse(album.json_data))
      })
    }

    // Navigating from album page to new search
    if ((params.id && !nextProps.params.id) || (location.query.path && !nextProps.location.query.path)) {
      const cached = lscache.get(QUERY_CACHE_KEY)
      if (cached) {
        replaceStructure(cached)
      } else {
        replaceStructure(emptyStructure)
      }
    }

    // Navigating between albums
    if (nextProps.params.id && params.id !== nextProps.params.id) {
      replaceStructure(JSON.parse(nextProps.album.json_data))
    }
    if (nextProps.location.query.path && location.query.path !== nextProps.location.query.path) {
      this.loadPath(nextProps.location.query.path)
    }

    // Load original album structure when coming back from edit mode
    if (params.id === nextProps.params.id && params.task === 'edit' && nextProps.params.task !== 'edit') {
      replaceStructure(JSON.parse(nextProps.album.json_data))
    }

    // Whenever hash changes
    if (hash !== nextProps.hash && !nextProps.images && !isEmptyStructure(nextProps.query.structure)) {
      searchImages(nextProps.query.structure)

      // If editing album or searching, save query to localStorage
      if (params.task === 'edit' || location.pathname === '/search') {
        lscache.set(QUERY_CACHE_KEY + (params.id || ''), nextProps.query.structure, QUERY_CACHE_EXPIRY)
      }
    }

    if (album.saving && !nextProps.album.saving) {
      this.setState({ saveSuccess: true })
    }

    // Forced pagination check on image load
    if (nextProps.images && (!images || images.length !== nextProps.images.length)) {
      this.onScroll()
    }

    if (params.task !== 'info' && nextProps.params.task === 'info' && !this.props.keywordMap) {
      this.props.fetchSearchOptions()
    }
  }
  componentWillUnmount () {
    const { location, params, replaceStructure } = this.props
    if (params.id) {
      replaceStructure(emptyStructure)
    }
    if (location.pathname === '/search') {
      lscache.remove(QUERY_CACHE_KEY)
    }
    window.removeEventListener('scroll', this.onScroll)
  }
  routerWillLeave = () => {
    const { params, modals } = this.props
    if (params.task === 'edit' && this.isUnsaved() && !modals[SAVE_ALBUM_MODAL_KEY]) {
      return 'Your changes have not been saved. Are you sure you want to leave?'
    }
  }
  loadPath (path) {
    const { replaceStructure } = this.props
    if (path === 'event:null') {
      replaceStructure({
        queries: [{
          ...emptyQuery,
          event: [{ value: null, operator: 'must' }]
        }]
      })
      return
    }
    if (path === 'keyword:null') {
      replaceStructure({
        queries: [{
          ...emptyQuery,
          keyword: [{ value: null, operator: 'must' }]
        }]
      })
      return
    }
    replaceStructure({
      queries: [{
        ...emptyQuery,
        path: [{
          value: path === 'null' ? null : path,
          operator: 'must'
        }]
      }]
    })
  }
  onScroll = () => {
    const { error } = this.props
    clearTimeout(this.scrollTimeout)
    this.scrollTimeout = setTimeout(() => {
      const { innerHeight } = window
      const { scrollHeight } = window.document.body
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop
      const diff = scrollHeight - (scrollTop + innerHeight)
      if (this.shouldLoadMoreImages() && !error && diff < PAGINATION_SENSITIVITY) {
        this.loadMoreImages()
      }
    }, 200)
  }
  shouldLoadMoreImages () {
    const { params, images, count, fetching } = this.props
    if (fetching) {
      return false
    }
    if (params.task && params.task !== 'edit') {
      return false
    }
    return images && count > images.length
  }
  loadMoreImages = limit => {
    const { searchImages, query, images } = this.props
    return searchImages(query.structure, (images || []).length, limit)
  }
  isUnsaved () {
    const { params, location, query, album } = this.props
    if (isEmptyStructure(query.structure)) {
      return false
    }
    if (location.pathname === '/search') {
      return true
    }
    if (params.task !== 'edit') {
      return false
    }
    return album.json_data && !isEqual(query.structure, JSON.parse(album.json_data))
  }
  isEditing () {
    const { location, params } = this.props
    return location.pathname === '/search' || params.task === 'edit'
  }
  setCover = imageID => {
    const { album, setAlbumCover } = this.props
    setAlbumCover(album, imageID)
  }
  getTitle () {
    const { album, location } = this.props
    if (location.query.path) {
      if (SPECIAL_ALBUM_PATH_TITLE[location.query.path]) {
        return SPECIAL_ALBUM_PATH_TITLE[location.query.path]
      }
      return location.query.path.split('/').pop().replace(/^\d+\.? /, '')
    }
    return album.name
  }
  enableEditing = () => {
    const { params, push } = this.props
    if (params.id && params.task !== 'edit') {
      const headerHeight = findDOMNode(this.refs.header).clientHeight
      push(`/album/${params.id}/edit`)
      const searchHeight = findDOMNode(this.refs.search).clientHeight

      // Going into edit mode will push the gallery downwards due to the
      // query form appearing above, so here we use the height difference
      // to scroll upwards and keep the gallery in the same position
      if (window.innerWidth < MOBILE_BREAKPOINT) {
        window.scrollBy(0, searchHeight - headerHeight + TOP_BAR_HEIGHT)
      }
    }
  }
  enableImageReorder = () => {
    const { query, enableImageReorder, shouldSearchImages, searchImages } = this.props
    enableImageReorder()
    // Preload manual search structure to prevent another spinner when reordering
    const structure = {
      ...query.structure,
      sort: {
        ...query.structure.sort,
        value: 'manual',
        operator: null
      }
    }
    if (shouldSearchImages(structure)) {
      searchImages(structure)
    }
  }
  renderMenuToggle = className => {
    return (
      <button
        data-toggle='album-menu'
        className={className}
        onClick={() => this.setState({ menuOpen: !this.state.menuOpen })}>
        <Icon icon='dots' />
      </button>
    )
  }
  renderHeader (title) {
    const {
      params,
      album,
      images,
      query
    } = this.props

    let cover
    if (album.cover) {
      cover = album.cover
    } else if (images && images.length !== 0) {
      cover = images[0]._id
    }

    return (
      <AlbumHeader
        {...this.props}
        ref='header'
        id={params.id}
        title={title}
        task={params.task}
        thumbSize={query.thumbSize}
        isFixed={query.structure.fixed && query.structure.fixed.length !== 0}
        cover={cover}
        menuOpen={this.state.menuOpen}
        renderMenuToggle={this.renderMenuToggle}
        hideCount={album.id && isEmptyStructure(query.structure)}
      />
    )
  }
  renderGallery () {
    const {
      album, params, images, query, anonymousUser, entities,
      getImageSource, selectImage, deselectImage, updateManualOrder, updateThumbSize
    } = this.props
    const limit = !images || params.task === 'info' ? 4 : images.length
    if (album.id && isEmptyStructure(query.structure)) {
      if (album.image_share) {
        return <ErrorMessage message='No images have been shared with you' />
      }
      return <ErrorMessage message='There are no search terms for this album' />
    }
    return (
      <Gallery
        entities={entities}
        images={images ? images.slice(0, limit) : null}
        getImageSource={getImageSource}
        selectImageMode={query.selectImageMode}
        reorderImageMode={query.reorderImageMode}
        selectedImages={query.selectedImages}
        selectImage={selectImage}
        deselectImage={deselectImage}
        updateManualOrder={updateManualOrder}
        anonymousUser={anonymousUser}
        setCover={this.setCover}
        enableEditing={this.enableEditing}
        title={this.getTitle() || 'Search results'}
        confirmReorder={query.structure.sort.value !== 'manual' && query.structure.sort.manual}
        showEventHeadings={query.structure.sort.value === 'event'}
        loadMoreImages={this.shouldLoadMoreImages() ? this.loadMoreImages : null}
        canShareImagesInternal={this.props.canShareImagesInternal}
        canShareImagesExternal={this.props.canShareImagesExternal}
        thumbSize={query.thumbSize}
        updateThumbSize={updateThumbSize}
        userLibraries={query.libraries}
        albumID={params.id}
        canEdit={this.props.canEdit}
        canFlag={this.props.canFlag}
        canRemove={this.props.canEdit && !this.props.location.query.path}
        excludeImages={this.props.excludeImages}
      />
    )
  }
  renderCancelButton () {
    const { album, previousPath } = this.props
    if (album && album.id) {
      return (
        <Link to={`/album/${album.id}`}>
          Cancel
        </Link>
      )
    }
    if (!previousPath || previousPath === '/login') {
      return <Link to='/'>Cancel</Link>
    }
    return (
      <button onClick={() => window.history.back()}>
        Cancel
      </button>
    )
  }
  renderEditHeader () {
    const { location, openModal, images } = this.props
    const search = location.pathname === '/search'
    return (
      <div className={classNames.header}>
        <div className={classNames.headerButtonsLeft}>
          {this.renderCancelButton()}
        </div>
        <div className={classNames.headerTitle}>
          {search ? 'Search' : 'Edit Album'}
        </div>
        <div className={classNames.headerButtonsRight}>
          {images && images.length !== 0 && this.renderMenuToggle()}
          {(!search || this.isUnsaved()) &&
            <button onClick={() => openModal(SAVE_ALBUM_MODAL_KEY)}>
              Save
            </button>
          }
        </div>
      </div>
    )
  }
  renderSelectHeader () {
    const { query, disableImageSelect, openModal, canSaveSelected } = this.props
    return (
      <div className={classNames.selectHeader}>
        <div className={classNames.headerButtonsLeft}>
          <button onClick={disableImageSelect}>
            <Icon icon='close' />
          </button>
        </div>
        <div className={classNames.headerTitleLeft}>
          {query.reorderImageMode
            ? 'Select new position'
            : query.selectedImages.length + ' selected'
          }
        </div>
        <div className={classNames.headerButtonsRight}>
          {!query.reorderImageMode &&
            <button onClick={this.enableImageReorder}>
              <Icon icon='shuffle' />
            </button>
          }
          {this.renderMenuToggle()}
          {canSaveSelected &&
            <button onClick={() => openModal(SAVE_ALBUM_MODAL_KEY)}>
              Save As
            </button>
          }
        </div>
      </div>
    )
  }
  renderCount (editing) {
    const { count, query, params } = this.props
    const { sort } = query.structure

    let value = sort.value
    if (sort.operator) {
      value += ` (${sort.operator})`
    }
    if (!query.selectImageMode) {
      value = (
        <button
          key='link'
          className={classNames.sortLink}
          onClick={() => this.setState({ sortMenuOpen: true })}>
          {value}
        </button>
      )
    }

    return (
      <span className={classNames.count}>
        {editing && pluralize(count, 'image') + ' found'}
        {sort.value && params.task !== 'info' &&
          [
            editing ? ', sorted by ' : 'Sorted by ',
            value
          ]
        }
      </span>
    )
  }
  renderMenu = () => {
    const { location, params, album, canShareInternal, canShareExternal, query } = this.props
    const editing = this.isEditing()
    return (
      <AlbumMenu
        {...this.props}
        fixed={editing || query.selectImageMode}
        open={this.state.menuOpen}
        closeMenu={() => this.setState({ menuOpen: false })}
        album={params.id ? album : null}
        title={this.getTitle()}
        editing={editing}
        loadMoreImages={this.loadMoreImages}
        setCover={this.setCover}
        canShare={canShareInternal || canShareExternal}
        canLock={location.pathname === '/search' || (editing && this.props.isOwnAlbum)}
      />
    )
  }
  renderContent () {
    const {
      location, params,
      album, owner, query, images, keywords, count, fetching, error,
      getImageSource, fetchAlbum,
      canShareInternal, canShareExternal, updateSortOrder, canSave
    } = this.props
    const { sortMenuOpen, saveSuccess } = this.state

    const editing = this.isEditing()
    const unsaved = this.isUnsaved()

    if (params.task === 'share') {
      return (
        <AlbumShare
          id={params.id}
          album={album}
          canShareInternal={canShareInternal}
          canShareExternal={canShareExternal}
          lists={this.props.lists}
          albumLists={this.props.albumLists}
          sort={query.structure.sort}
          canSave={canSave && query.selectedImages.length === 0}
          showFixHint={this.props.canEdit && this.props.isOwnAlbum && (!query.structure.fixed || query.structure.fixed.length === 0 && count <= MAX_FIXED_COUNT)}
          loadAllImages={() => {
            if (images.length === count) {
              return Promise.resolve()
            }
            return this.loadMoreImages(count)
          }}
          images={images}
          query={query}
        />
      )
    }

    if (params.task === 'groups') {
      return (
        <AlbumLists
          id={params.id}
          lists={this.props.lists}
          albumLists={this.props.albumLists}
        />
      )
    }

    return (
      <div>
        {this.props.location.pathname === '/search' && count > 0 &&
          <input
            className={classNames.slider}
            type='range'
            min={80}
            max={640}
            defaultValue={query.thumbSize}
            onMouseUp={e => this.props.updateThumbSize(parseInt(e.target.value))}
          />
        }
        {editing &&
          <ImageSearch
            ref='search'
            query={query}
            autoFocus={this.props.location.pathname === '/search'}
            className={classNames.search}
          />
        }
        <div>
          {unsaved && count > 0 &&
            <span className={classNames.unsaved}>
              Unsaved
            </span>
          }
          {!unsaved && !editing && saveSuccess &&
            <span className={classNames.saveSuccess}>
              Saved
            </span>
          }
          <AlbumBreadcrumbs location={this.props.location} />
          {count > 0 && params.task !== 'info' &&
            this.renderCount(editing)
          }
          <SortMenu
            open={sortMenuOpen}
            closeMenu={() => this.setState({ sortMenuOpen: false })}
            updateSortOrder={updateSortOrder}
            manualDisabled={!query.structure.sort.manual}
          />
          {params.task !== 'info' && album.fetching && <LoadingSpinner />}
          {album.fetchError &&
            <ErrorMessage
              message={
                album.fetchError.status === 403
                ? 'You do not have permission to view this album'
                : 'There was an error fetching the album'
              }
              onRetry={() => fetchAlbum(params.id)}
            />
          }
          {params.task !== 'info' &&
            this.renderGallery()
          }
          {error &&
            <ErrorMessage
              message='There was an error fetching images'
              onRetry={() => this.loadMoreImages()}
            />
          }
          {params.task !== 'info' &&
            <LoadingSpinner show={!!fetching} />
          }
          {params.task === 'info' &&
            <AlbumInfo
              {...this.props}
              loadMoreImages={this.loadMoreImages}
              onClickSave={() => this.props.openModal(SAVE_ALBUM_MODAL_KEY)}
            />
          }
          {editing && this.renderEditHeader()}
          {query.selectImageMode && !location.query.path && (!params.id || this.props.canEdit) && this.renderSelectHeader()}
        </div>
      </div>
    )
  }
  render () {
    const editing = this.isEditing()
    const title = this.getTitle()

    return (
      <div>
        {title && !editing &&
          this.renderHeader(title)
        }
        {this.renderMenu()}
        {this.renderContent()}
      </div>
    )
  }
}

const mapStateToProps = ({ entities, search, auth, query, albums, modals, playlists, nav }, { params, location }) => {
  const album = entities.albums[params.id] || {}
  const hash = getHash(query.structure)
  const { images, keywords, count, fetching, error, resolvedKeywords } = getImages(search, hash, query, entities, album)
  const isOwnAlbum = auth.user && album.user_id === auth.user.id
  const locked = album.favourite || album.image_share
  const anonymousUser = isAnonymous(auth.user)
  const lists = mapStateToLists(playlists, entities)
  return {
    lists,
    entities,
    album,
    hash,
    query,
    images,
    keywords,
    count: getCount(count, query),
    trueCount: count, // Used when fetching all images
    fetching,
    error,
    modals,
    isOwnAlbum,
    owner: entities.users[album.user_id],
    getImageSource: createImageGetter(auth),
    hasFavourited: (albums.favourites.data || []).indexOf(params.id) !== -1,
    canEdit: !!params.id && !anonymousUser && userHasPerm(auth.user, 'search_and_save'),
    canSave: !album.favourite && (isOwnAlbum || userHasPerm(auth.user, 'edit_collections')),
    canSaveSelected: userHasPerm(auth.user, 'search_and_save'),
    canDelete: !!params.id && !query.selectImageMode && !anonymousUser,
    canShareInternal: canShareInternal(params, auth, isOwnAlbum),
    canShareExternal: !!params.id && isOwnAlbum && userHasPerm(auth.user, 'external_share'),
    showShare: !!params.id && (userHasPerm(auth.user, 'external_share') || userHasPerm(auth.user, 'internal_share') || userHasPerm(auth.user, 'internal_share_all')),
    canShareImagesInternal: userHasPerm(auth.user, 'internal_share'),
    canShareImagesExternal: userHasPerm(auth.user, 'external_share'),
    canDownload: canDownload(auth),
    canFlag: userHasPerm(auth.user, 'flag_image'),
    anonymousUser,
    addingToPlaylist: location.query.playlist ? (entities.playlists[location.query.playlist] || { id: 'create' }) : null,
    albumLists: params.id && lists && lists.filter(list => {
      return list.albums.map(a => a.id).indexOf(album.id) !== -1
    }).map(list => list.id),
    shouldSearchImages: structure => {
      const hash = getHash(structure)
      return !search[hash] || !search[hash].data
    },
    shouldFetchAlbums: !albums.all.data,
    previousPath: nav.previousPath,
    resolvedKeywords,
    keywordMap: search.options.keywordMap,
    hasDynamicLink: hasDynamicLink(query.structure)
  }
}

function canShareInternal (params, auth, isOwnAlbum) {
  if (!params.id) return false
  if (userHasPerm(auth.user, 'internal_share_all')) return true
  if (userHasPerm(auth.user, 'internal_share')) return isOwnAlbum
  return false
}

function getImages (search, hash, query, entities, album) {
  if (isEmptyStructure(query.structure) || !search[hash]) {
    return {}
  }
  if (!search[hash].data) {
    return search[hash]
  }

  let images = search[hash].data
    .filter(id => query.structure.exclude.indexOf(id) === -1)

  const sort = query.structure.sort
  if (sort.value === 'manual' && sort.manual) {
    images = sortWithGuide(images, sort.manual)
  } else if (album.image_share) {
    images = sortWithGuide(images, query.structure.include)
  }

  return {
    ...search[hash],
    images: images.map(id => entities.images[id])
  }
}

function sortWithGuide (array, order) {
  return array.slice(0).sort((a, b) => {
    // Sort images using the manual sort array as a guide
    var indexA = order.indexOf(a)
    var indexB = order.indexOf(b)
    if (indexA !== -1 && indexB === -1) return -1
    if (indexA === -1 && indexB !== -1) return 1
    if (indexA < indexB) return -1
    if (indexA > indexB) return 1
    return 0
  })
}

function getCount (count, query) {
  const { fixed, exclude } = query.structure
  if (fixed && fixed.length !== 0) {
    return fixed.length
  }
  return count - exclude.length
}

const mapDispatchToProps = {
  fetchAlbum,
  fetchAlbums,
  setFavouriteAlbum,
  searchImages,
  replaceStructure,
  selectImage,
  deselectImage,
  disableImageSelect,
  updateSortOrder,
  updateManualOrder,
  updateThumbSize,
  setAlbumCover,
  openModal,
  push,
  enableImageReorder,
  fixAlbum,
  unfixAlbum,
  excludeImages,
  fetchSearchOptions
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Album))



// WEBPACK FOOTER //
// ./src/pages/Album.js